/**
 * `AccountsListView` — the admin's primary destination.
 *
 * GET /internal/admin/accounts with `?search=&limit=&offset=&sort=&dir=`.
 * Server-side sort keys: name | created_at | last_active_at | last_login_at |
 * status | owner_email | site_count (default created_at desc).
 *
 * Responsive layout:
 *   - sm+ (≥640px) — a table with toggleable columns: Name (always),
 *     Owner, Domains, Status, Internal, Region, Signed up, Last login,
 *     Last active. Header clicks on sortable columns (Name, Owner,
 *     Status, Signed up, Last login, Last active) toggle sort dir; an
 *     active-column chevron makes the state legible. The container is
 *     `overflow-x-auto` so a narrow viewport scrolls horizontally
 *     instead of bleeding off-screen.
 *   - <sm (mobile) — a stacked card list with a small sort `<select>`
 *     above the cards (header clicks don't translate to cards). Column
 *     toggles hide the matching field inside the card too.
 *
 * Persisted to localStorage (per-staff, per-browser):
 *   - `sill.admin.accounts.sort`    → "<key>:<dir>" (e.g. "created_at:desc")
 *   - `sill.admin.accounts.columns` → comma-separated visible keys
 *
 * `last_active_at` is the MAX(evaluated_at) of the account's real
 * audit_record rows (AGENT TRAFFIC); NULL when the account has never
 * recorded real activity.
 *
 * `last_login_at` is the MAX(user.last_active_at) across every user on
 * the account — the HUMAN dashboard-login signal — distinct from
 * `last_active_at`. NULL when no user has ever loaded the dashboard.
 * Both are rendered in both layouts; matching `title=` tooltips on the
 * column headers + mobile labels keep the two unambiguous.
 *
 * Search: a 300ms-debounced input that re-issues the list call with
 * the new `?search`. Pagination: `limit` is fixed at 25; Prev/Next
 * step `offset` by `limit`. Changing sort or search resets to page 1.
 * The server's response carries `total` so we can disable Next when
 * we're on the last page.
 *
 * Empty + error states render inline; not a route. A `service_error`
 * banner replaces the table when the call fails so staff don't see
 * a misleading "0 accounts" row.
 */

import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  AdminAccountSummary,
  AdminAccountsListDir,
  AdminAccountsListSort,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import type { SetView } from '../../app/view-router.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill, statusTone } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';
import { formatIsoCompact, formatLastActive } from '../../lib/format.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

const PAGE_SIZE = 25;

/* ───────────────────────── column model ─────────────────────────
 *
 * Single source of truth for which columns exist, which are
 * toggleable, which are sortable, and how each renders. Keeping the
 * model declarative makes the visibility + sort wiring trivial: the
 * `<th>` row maps over `ALL_COLUMNS.filter(visible)`, the dropdown
 * maps over `ALL_COLUMNS.filter(c => c.toggleable)`, and the row
 * cells likewise filter on `visible`. */

type ColumnKey =
  | 'name'
  | 'owner'
  | 'domains'
  | 'status'
  | 'internal'
  | 'region'
  | 'created_at'
  | 'last_active'
  | 'last_login';

interface ColumnDef {
  readonly key: ColumnKey;
  readonly label: string;
  readonly width: string;
  /** False for `name` — always visible. */
  readonly toggleable: boolean;
  /** When set, the column header is clickable and sorts by the server
   *  key. Not every column maps to a server sort (domains, region). */
  readonly sortKey: AdminAccountsListSort | null;
  /** Optional disambiguation tooltip — surfaces via `title=` on the
   *  header + the matching mobile-card label. Reserved for columns
   *  whose meaning isn't obvious from the short label alone (today:
   *  the "Last login" vs "Last active" pair, which read similar but
   *  measure different signals). */
  readonly title?: string;
}

/** Tooltip copy for the two "last …" columns. Kept as top-level
 *  constants so the table header, the mobile card meta, and any future
 *  surface (e.g. the columns menu) share one source of truth and stay
 *  in lockstep if the wording changes. */
const TITLE_LAST_LOGIN =
  'Most recent dashboard login by any user on this account';
const TITLE_LAST_ACTIVE =
  'Most recent agent activity (audit record) on this account';

const ALL_COLUMNS: readonly ColumnDef[] = [
  { key: 'name', label: 'Name', width: '18%', toggleable: false, sortKey: 'name' },
  { key: 'owner', label: 'Owner', width: '14%', toggleable: true, sortKey: 'owner_email' },
  { key: 'domains', label: 'Domains', width: '18%', toggleable: true, sortKey: null },
  { key: 'status', label: 'Status', width: '8%', toggleable: true, sortKey: 'status' },
  { key: 'internal', label: 'Internal', width: '7%', toggleable: true, sortKey: null },
  { key: 'region', label: 'Region', width: '7%', toggleable: true, sortKey: null },
  { key: 'created_at', label: 'Signed up', width: '10%', toggleable: true, sortKey: 'created_at' },
  {
    key: 'last_login',
    label: 'Last login',
    width: '12%',
    toggleable: true,
    sortKey: 'last_login_at',
    title: TITLE_LAST_LOGIN,
  },
  {
    key: 'last_active',
    label: 'Last active',
    width: '12%',
    toggleable: true,
    sortKey: 'last_active_at',
    title: TITLE_LAST_ACTIVE,
  },
];

const DEFAULT_VISIBLE: ReadonlySet<ColumnKey> = new Set<ColumnKey>([
  'name',
  'owner',
  'domains',
  'status',
  'internal',
  'created_at',
  'last_login',
  'last_active',
  // `region` is intentionally OFF by default — Phase 1 ships US-East
  // only, so the column is uniformly "us-east-1" and burns horizontal
  // space. Staff can opt in via the Columns menu.
]);

const ALL_COLUMN_KEYS = new Set<ColumnKey>(ALL_COLUMNS.map((c) => c.key));

/* ───────────────────────── persistence ───────────────────────── */

const SORT_STORAGE_KEY = 'sill.admin.accounts.sort';
const COLUMNS_STORAGE_KEY = 'sill.admin.accounts.columns';

const VALID_SORTS: ReadonlySet<AdminAccountsListSort> = new Set<AdminAccountsListSort>([
  'name',
  'created_at',
  'last_active_at',
  'last_login_at',
  'status',
  'owner_email',
  'site_count',
]);

interface SortState {
  readonly sort: AdminAccountsListSort;
  readonly dir: AdminAccountsListDir;
}

const DEFAULT_SORT: SortState = { sort: 'created_at', dir: 'desc' };

function readStoredSort(): SortState {
  if (typeof window === 'undefined') return DEFAULT_SORT;
  try {
    const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (raw === null) return DEFAULT_SORT;
    const [sortRaw, dirRaw] = raw.split(':');
    if (
      sortRaw !== undefined &&
      VALID_SORTS.has(sortRaw as AdminAccountsListSort) &&
      (dirRaw === 'asc' || dirRaw === 'desc')
    ) {
      return { sort: sortRaw as AdminAccountsListSort, dir: dirRaw };
    }
  } catch {
    /* storage disabled */
  }
  return DEFAULT_SORT;
}

function writeStoredSort(state: SortState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SORT_STORAGE_KEY, `${state.sort}:${state.dir}`);
  } catch {
    /* storage disabled */
  }
}

function readStoredColumns(): ReadonlySet<ColumnKey> {
  if (typeof window === 'undefined') return DEFAULT_VISIBLE;
  try {
    const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (raw === null) return DEFAULT_VISIBLE;
    const parts = raw.split(',').map((s) => s.trim());
    const out = new Set<ColumnKey>(['name']); // always-visible
    for (const p of parts) {
      if (ALL_COLUMN_KEYS.has(p as ColumnKey)) {
        out.add(p as ColumnKey);
      }
    }
    // Defensive: if every toggleable was somehow hidden by stored state
    // older than the schema, fall back to defaults rather than ship
    // a name-only table.
    if (out.size === 1) return DEFAULT_VISIBLE;
    return out;
  } catch {
    /* storage disabled */
  }
  return DEFAULT_VISIBLE;
}

function writeStoredColumns(cols: ReadonlySet<ColumnKey>): void {
  if (typeof window === 'undefined') return;
  try {
    const ordered = ALL_COLUMNS.filter((c) => cols.has(c.key)).map((c) => c.key);
    window.localStorage.setItem(COLUMNS_STORAGE_KEY, ordered.join(','));
  } catch {
    /* storage disabled */
  }
}

/* ───────────────────────── view ───────────────────────── */

export interface AccountsListViewProps {
  setView: SetView;
}

export function AccountsListView({ setView }: AccountsListViewProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  // Accounts whose deletion completed are anonymized shells kept for the
  // audit chain; hidden unless staff ask for them.
  const [showDeleted, setShowDeleted] = useState(false);
  const [rows, setRows] = useState<readonly AdminAccountSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortState, setSortState] = useState<SortState>(() => readStoredSort());
  const [visibleColumns, setVisibleColumns] = useState<ReadonlySet<ColumnKey>>(() =>
    readStoredColumns(),
  );
  // One-shot "deleted" flash: AccountDetailView writes a `?deleted=…`
  // search param onto the URL before calling setView('accounts'); we
  // read it on mount, surface the toast, then strip the param so a
  // refresh doesn't re-fire the message.
  const [deletedFlash, setDeletedFlash] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const url = new URL(window.location.href);
    const v = url.searchParams.get('deleted');
    if (v !== null && v.length > 0) {
      url.searchParams.delete('deleted');
      window.history.replaceState({}, '', url.toString());
      return v;
    }
    return null;
  });
  useEffect(() => {
    if (deletedFlash === null) return;
    const id = setTimeout(() => setDeletedFlash(null), 5000);
    return () => clearTimeout(id);
  }, [deletedFlash]);

  // Debounce the search input — 300ms is the dashboard's preferred
  // value and keeps the network quiet while staff type.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setOffset(0); // Reset to first page on every new search.
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async (): Promise<void> => {
      try {
        const res = await api.listAccounts({
          ...(debouncedSearch.length > 0 ? { search: debouncedSearch } : {}),
          limit: PAGE_SIZE,
          offset,
          sort: sortState.sort,
          dir: sortState.dir,
          ...(showDeleted ? { include_deleted: true } : {}),
        });
        if (cancelled) return;
        setRows(res.accounts);
        setTotal(res.total);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') {
          // The HTTP client's on_unauthorized already routed to
          // login; nothing to do here.
          return;
        }
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setError(`Could not load accounts${reason}.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, debouncedSearch, offset, sortState, showDeleted]);

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + rows.length, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  /** Apply a new sort by toggling on repeat-same-key, otherwise pick a
   *  natural default direction (desc for date-like columns, asc for
   *  text). Used by the desktop sortable headers. Resets to page 1 and
   *  triggers a refetch via the effect's dep array. */
  const applySort = useCallback(
    (key: AdminAccountsListSort): void => {
      setSortState((cur) => {
        let next: SortState;
        if (cur.sort === key) {
          next = { sort: key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
        } else {
          const dateLike =
            key === 'created_at' ||
            key === 'last_active_at' ||
            key === 'last_login_at';
          next = { sort: key, dir: dateLike ? 'desc' : 'asc' };
        }
        writeStoredSort(next);
        return next;
      });
      setOffset(0);
    },
    [],
  );

  /** Apply a precise sort+dir tuple — used by the mobile `<select>`
   *  where the user is picking the explicit direction (e.g. "Signed
   *  up — newest" vs "— oldest"), so we want the chosen dir verbatim
   *  rather than the toggle semantics of `applySort`. */
  const applySortExact = useCallback((next: SortState): void => {
    setSortState(() => {
      writeStoredSort(next);
      return next;
    });
    setOffset(0);
  }, []);

  const toggleColumn = useCallback((key: ColumnKey): void => {
    setVisibleColumns((cur) => {
      if (key === 'name') return cur; // Name is non-toggleable.
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeStoredColumns(next);
      return next;
    });
  }, []);

  const visibleCols = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleColumns.has(c.key)),
    [visibleColumns],
  );

  const handleRowClick = (id: string): void => {
    setView('account-detail', { accountId: id });
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Accounts
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-6">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(28px, 6vw, 44px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
              margin: 0,
            }}
          >
            All accounts
          </h1>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 14,
              color: C.muted,
              letterSpacing: 0.5,
            }}
            aria-label={`${total} accounts match the current search`}
          >
            ({total})
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              minHeight: 36,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => {
                setShowDeleted(e.target.checked);
                setOffset(0);
              }}
            />
            Show deleted
          </label>
          <div
            className="flex items-center gap-2 px-3 py-2 flex-1 sm:flex-initial"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            <Search size={13} color={C.muted} />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, domain, or owner"
              className="flex-1 outline-none bg-transparent w-full"
              style={{
                fontFamily: monoFont,
                fontSize: 12,
                color: C.text,
                minWidth: 0,
              }}
              aria-label="Search accounts"
            />
          </div>
          <ColumnsMenu
            visible={visibleColumns}
            onToggle={toggleColumn}
          />
        </div>
      </div>

      {/* Mobile-only sort selector — header clicks don't translate to
          stacked cards so a plain select keeps sorting reachable on
          phones without inventing a second UI. */}
      <div className="sm:hidden mb-3">
        <MobileSortSelect value={sortState} onChange={applySortExact} />
      </div>

      {deletedFlash !== null ? (
        <div
          className="p-3 mb-4"
          style={{
            background: C.surface2,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
          }}
          role="status"
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.limeFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Account deleted
          </div>
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.body }}>
            {deletedFlash}
          </div>
        </div>
      ) : null}

      {error !== null ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.coralFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Load failed
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>{error}</div>
        </Card>
      ) : null}

      {/* Desktop / tablet — the table */}
      <Card className="hidden sm:block" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              fontFamily: sansFont,
              width: '100%',
              minWidth: 880,
            }}
          >
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {visibleCols.map((cell) => (
                  <SortableHeader
                    key={cell.key}
                    column={cell}
                    sortState={sortState}
                    onSort={applySort}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleCols.length}
                    className="px-4 py-10 text-center"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      color: C.muted,
                      letterSpacing: 1.5,
                    }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleCols.length}
                    className="px-4 py-10 text-center"
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.muted,
                    }}
                  >
                    {debouncedSearch.length > 0
                      ? `No accounts match "${debouncedSearch}".`
                      : 'No accounts yet.'}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <AccountTableRow
                    key={row.account_id}
                    row={row}
                    isLast={idx === rows.length - 1}
                    visibleColumns={visibleColumns}
                    onClick={(): void => handleRowClick(row.account_id)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile — stacked cards */}
      <div className="sm:hidden">
        {loading && rows.length === 0 ? (
          <Card style={{ padding: 24 }}>
            <div
              className="text-center"
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.muted,
                letterSpacing: 1.5,
              }}
            >
              Loading…
            </div>
          </Card>
        ) : rows.length === 0 ? (
          <Card style={{ padding: 24 }}>
            <div
              className="text-center"
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
              }}
            >
              {debouncedSearch.length > 0
                ? `No accounts match "${debouncedSearch}".`
                : 'No accounts yet.'}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {rows.map((row) => (
              <AccountMobileCard
                key={row.account_id}
                row={row}
                visibleColumns={visibleColumns}
                onClick={(): void => handleRowClick(row.account_id)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1,
          }}
        >
          {total === 0
            ? '0 accounts'
            : `${showingFrom}–${showingTo} of ${total}`}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={!canPrev || loading}
            className="flex items-center gap-1 px-3"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontSize: 12,
              color: canPrev ? C.text : C.dim,
              opacity: canPrev ? 1 : 0.5,
              minHeight: 40,
            }}
            aria-label="Previous page"
          >
            <ChevronLeft size={13} /> Prev
          </button>
          <button
            type="button"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!canNext || loading}
            className="flex items-center gap-1 px-3"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontSize: 12,
              color: canNext ? C.text : C.dim,
              opacity: canNext ? 1 : 0.5,
              minHeight: 40,
            }}
            aria-label="Next page"
          >
            Next <ChevronRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── sortable header ───────────────────────── */

function SortableHeader({
  column,
  sortState,
  onSort,
}: {
  column: ColumnDef;
  sortState: SortState;
  onSort: (key: AdminAccountsListSort) => void;
}): JSX.Element {
  const C = useC();
  const isActive =
    column.sortKey !== null && sortState.sort === column.sortKey;
  const isSortable = column.sortKey !== null;

  const baseStyle = {
    width: column.width,
    fontFamily: monoFont,
    fontSize: 9,
    color: isActive ? C.text : C.muted,
    letterSpacing: 2,
    textTransform: 'uppercase' as const,
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
  };

  if (!isSortable) {
    return (
      <th
        className="text-left px-4 py-3"
        style={baseStyle}
        title={column.title}
      >
        {column.label}
      </th>
    );
  }

  // sortKey is non-null here — bind it for the click handler so we
  // don't redo the narrowing in JSX.
  const sortKey = column.sortKey as AdminAccountsListSort;

  return (
    <th
      className="text-left px-4 py-3"
      style={{ ...baseStyle, padding: 0 }}
      title={column.title}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="text-left w-full inline-flex items-center gap-1 px-4 py-3"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: isActive ? C.text : C.muted,
          fontFamily: monoFont,
          fontSize: 9,
          letterSpacing: 2,
          textTransform: 'uppercase',
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
        aria-label={`Sort by ${column.label}${
          isActive
            ? `, currently ${sortState.dir === 'asc' ? 'ascending' : 'descending'}`
            : ''
        }`}
        aria-sort={
          isActive
            ? sortState.dir === 'asc'
              ? 'ascending'
              : 'descending'
            : 'none'
        }
        title={column.title}
      >
        <span>{column.label}</span>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            opacity: isActive ? 1 : 0.35,
            color: isActive ? C.limeFg : C.muted,
          }}
        >
          {isActive && sortState.dir === 'asc' ? (
            <ChevronUp size={11} />
          ) : (
            <ChevronDown size={11} />
          )}
        </span>
      </button>
    </th>
  );
}

/* ───────────────────────── columns menu ───────────────────────── */

function ColumnsMenu({
  visible,
  onToggle,
}: {
  visible: ReadonlySet<ColumnKey>;
  onToggle: (key: ColumnKey) => void;
}): JSX.Element {
  const C = useC();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const close = useCallback((): void => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (wrapperRef.current === null) return;
      if (e.target instanceof Node && wrapperRef.current.contains(e.target)) {
        return;
      }
      close();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const toggleable = ALL_COLUMNS.filter((c) => c.toggleable);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 px-3"
        style={{
          background: 'transparent',
          color: C.muted,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          minHeight: 40,
          fontFamily: monoFont,
          fontSize: 11,
          letterSpacing: 1.2,
          fontWeight: 500,
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        <SlidersHorizontal size={13} aria-hidden="true" />
        Columns
      </button>
      {open ? (
        <div
          role="menu"
          aria-label="Toggle columns"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 200,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            zIndex: 40,
            padding: 4,
          }}
        >
          {toggleable.map((c) => {
            const checked = visible.has(c.key);
            return (
              <label
                key={c.key}
                role="menuitemcheckbox"
                aria-checked={checked}
                className="flex items-center gap-3"
                style={{
                  padding: '10px 10px',
                  minHeight: 40,
                  cursor: 'pointer',
                  color: C.text,
                  borderRadius: 2,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLLabelElement).style.background = C.surface2;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLLabelElement).style.background = 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(c.key)}
                  style={{
                    width: 14,
                    height: 14,
                    accentColor: C.limeFg,
                    cursor: 'pointer',
                  }}
                />
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  {c.label}
                </span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/* ───────────────────────── mobile sort select ───────────────────────── */

function MobileSortSelect({
  value,
  onChange,
}: {
  value: SortState;
  onChange: (next: SortState) => void;
}): JSX.Element {
  const C = useC();
  // On mobile we collapse the two-axis (key + dir) into a flat list so
  // staff can pick "Signed up (newest)" in one tap rather than two.
  const options: ReadonlyArray<{
    readonly id: string;
    readonly label: string;
    readonly sort: AdminAccountsListSort;
    readonly dir: AdminAccountsListDir;
  }> = [
    { id: 'created_at:desc', label: 'Signed up — newest', sort: 'created_at', dir: 'desc' },
    { id: 'created_at:asc', label: 'Signed up — oldest', sort: 'created_at', dir: 'asc' },
    { id: 'last_login_at:desc', label: 'Last login — recent', sort: 'last_login_at', dir: 'desc' },
    { id: 'last_login_at:asc', label: 'Last login — oldest', sort: 'last_login_at', dir: 'asc' },
    { id: 'last_active_at:desc', label: 'Last active — recent', sort: 'last_active_at', dir: 'desc' },
    { id: 'last_active_at:asc', label: 'Last active — oldest', sort: 'last_active_at', dir: 'asc' },
    { id: 'name:asc', label: 'Name — A → Z', sort: 'name', dir: 'asc' },
    { id: 'name:desc', label: 'Name — Z → A', sort: 'name', dir: 'desc' },
    { id: 'owner_email:asc', label: 'Owner — A → Z', sort: 'owner_email', dir: 'asc' },
    { id: 'status:asc', label: 'Status — A → Z', sort: 'status', dir: 'asc' },
  ];

  const currentId = `${value.sort}:${value.dir}`;
  // If the current state isn't one of the canned options (e.g. a
  // desktop user toggled "owner — desc" then opened mobile), fall the
  // select back to a neutral "Custom" entry so the dropdown still
  // reflects state without silently overwriting it on first paint.
  const hasMatch = options.some((o) => o.id === currentId);

  return (
    <label className="block w-full">
      <span className="sr-only">Sort accounts</span>
      <div
        className="flex items-center gap-2 px-3"
        style={{
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          minHeight: 40,
        }}
      >
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Sort
        </span>
        <select
          value={hasMatch ? currentId : '__custom__'}
          onChange={(e) => {
            const opt = options.find((o) => o.id === e.target.value);
            if (opt === undefined) return;
            if (value.sort === opt.sort && value.dir === opt.dir) return;
            onChange({ sort: opt.sort, dir: opt.dir });
          }}
          className="flex-1 bg-transparent outline-none"
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.text,
            minHeight: 40,
            border: 'none',
            paddingTop: 6,
            paddingBottom: 6,
          }}
          aria-label="Sort accounts"
        >
          {!hasMatch ? (
            <option value="__custom__">
              {`${value.sort} — ${value.dir}`}
            </option>
          ) : null}
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </label>
  );
}

/* ───────────────────────── table row ───────────────────────── */

function AccountTableRow({
  row,
  isLast,
  visibleColumns,
  onClick,
}: {
  row: AdminAccountSummary;
  isLast: boolean;
  visibleColumns: ReadonlySet<ColumnKey>;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer"
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${C.borderSoft}`,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.surface2)}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {visibleColumns.has('name') ? (
        <td className="px-4 py-3">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
            }}
          >
            {row.name}
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.dim,
              marginTop: 2,
            }}
          >
            {row.account_id}
          </div>
        </td>
      ) : null}
      {visibleColumns.has('owner') ? (
        <td className="px-4 py-3">
          {(row.owner_email ?? null) === null ? (
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.dim }}>—</span>
          ) : (
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.body,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 220,
              }}
              title={row.owner_email ?? ''}
            >
              {row.owner_email}
            </div>
          )}
        </td>
      ) : null}
      {visibleColumns.has('domains') ? (
        <td className="px-4 py-3">
          {row.domains.length === 0 ? (
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.dim }}>
              no sites
            </span>
          ) : (
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.body,
                lineHeight: 1.5,
              }}
            >
              {row.domains.slice(0, 3).join(', ')}
              {row.domains.length > 3 ? ` (+${row.domains.length - 3})` : ''}
            </div>
          )}
        </td>
      ) : null}
      {visibleColumns.has('status') ? (
        <td className="px-4 py-3">
          <Pill tone={statusTone(row.status)}>{row.status}</Pill>
        </td>
      ) : null}
      {visibleColumns.has('internal') ? (
        <td className="px-4 py-3">
          {row.is_internal ? (
            <Pill tone="amber">Internal</Pill>
          ) : (
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.dim }}>—</span>
          )}
        </td>
      ) : null}
      {visibleColumns.has('region') ? (
        <td
          className="px-4 py-3"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.body,
          }}
        >
          {row.region}
        </td>
      ) : null}
      {visibleColumns.has('created_at') ? (
        <td
          className="px-4 py-3"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            whiteSpace: 'nowrap',
          }}
        >
          {formatIsoCompact(row.created_at)}
        </td>
      ) : null}
      {visibleColumns.has('last_login') ? (
        <td
          className="px-4 py-3"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: row.last_login_at === null ? C.dim : C.body,
            whiteSpace: 'nowrap',
          }}
          title={TITLE_LAST_LOGIN}
        >
          {formatLastActive(row.last_login_at)}
        </td>
      ) : null}
      {visibleColumns.has('last_active') ? (
        <td
          className="px-4 py-3"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: row.last_active_at === null ? C.dim : C.body,
            whiteSpace: 'nowrap',
          }}
          title={TITLE_LAST_ACTIVE}
        >
          {formatLastActive(row.last_active_at)}
        </td>
      ) : null}
    </tr>
  );
}

/* ───────────────────────── mobile card ───────────────────────── */

function AccountMobileCard({
  row,
  visibleColumns,
  onClick,
}: {
  row: AdminAccountSummary;
  visibleColumns: ReadonlySet<ColumnKey>;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  const showOwner = visibleColumns.has('owner');
  const showDomains = visibleColumns.has('domains');
  const showStatus = visibleColumns.has('status');
  const showInternal = visibleColumns.has('internal');
  const showCreated = visibleColumns.has('created_at');
  const showLastLogin = visibleColumns.has('last_login');
  const showLastActive = visibleColumns.has('last_active');
  const showMetaRow = showCreated || showLastLogin || showLastActive;

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 44,
        // A flex column item defaults to min-width:auto (= its widest
        // unbreakable child), which let a long owner email push the card
        // past the screen edge on phones.
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <div className="flex items-start justify-between gap-2 min-w-0 w-full">
        <div className="min-w-0 flex-1">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              fontWeight: 500,
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {row.name}
          </div>
          {showOwner && row.owner_email !== null ? (
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.muted,
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={row.owner_email}
            >
              {row.owner_email}
            </div>
          ) : null}
        </div>
        {(showStatus || (showInternal && row.is_internal)) ? (
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {showStatus ? <Pill tone={statusTone(row.status)}>{row.status}</Pill> : null}
            {showInternal && row.is_internal ? <Pill tone="amber">Internal</Pill> : null}
          </div>
        ) : null}
      </div>

      {showDomains ? (
        row.domains.length > 0 ? (
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.body,
              lineHeight: 1.5,
              wordBreak: 'break-all',
            }}
          >
            {row.domains.slice(0, 3).join(', ')}
            {row.domains.length > 3 ? ` (+${row.domains.length - 3})` : ''}
          </div>
        ) : (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.dim }}>
            no sites
          </div>
        )
      ) : null}

      {showMetaRow ? (
        <div
          className="flex items-center justify-between gap-3 flex-wrap"
          style={{
            borderTop: `1px solid ${C.borderSoft}`,
            paddingTop: 8,
          }}
        >
          {showCreated ? (
            <MobileMeta
              label="Signed up"
              value={formatIsoCompact(row.created_at)}
              dim={false}
            />
          ) : null}
          {showLastLogin ? (
            <MobileMeta
              label="Last login"
              value={formatLastActive(row.last_login_at)}
              dim={row.last_login_at === null}
              title={TITLE_LAST_LOGIN}
            />
          ) : null}
          {showLastActive ? (
            <MobileMeta
              label="Last active"
              value={formatLastActive(row.last_active_at)}
              dim={row.last_active_at === null}
              title={TITLE_LAST_ACTIVE}
            />
          ) : null}
        </div>
      ) : null}
    </button>
  );
}

function MobileMeta({
  label,
  value,
  dim,
  title,
}: {
  label: string;
  value: string;
  dim: boolean;
  /** Optional disambiguation tooltip — same copy as the desktop column
   *  header. Long-press surfaces the title on most mobile browsers,
   *  and screen readers read it as the accessible name when present. */
  title?: string;
}): JSX.Element {
  const C = useC();
  return (
    <div className="flex flex-col gap-0.5" title={title}>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: dim ? C.dim : C.body,
        }}
      >
        {value}
      </span>
    </div>
  );
}
